// ─────────────────────────────────────────────────────────────────────────────
// PATCH: src/app/components/form-builder/types.ts
// Add these fields to the Form interface and FormSubmission interface,
// and update DEFAULT_FORM.
// ─────────────────────────────────────────────────────────────────────────────

// 1. ── Inside the `Form` interface, after `encrypt_submissions` ───────────────
//
//   // Approval workflow
//   approval_enabled?:              boolean;
//   approval_send_approved_email?:  boolean;
//   approval_send_rejected_email?:  boolean;
//   approval_require_note?:         boolean;
//   approval_email_field?:          string;   // form field name that holds submitter email
//   approval_from_name?:            string;
//   approval_email_subject?:        string;
//   approval_email_body?:           string;
//   rejection_email_subject?:       string;
//   rejection_email_body?:          string;
//
// ─────────────────────────────────────────────────────────────────────────────

// 2. ── Inside `FormSubmission`, extend the `status` union ─────────────────────
//
//   status: 'new' | 'read' | 'starred' | 'archived' | 'spam'
//        | 'pending' | 'approved' | 'rejected';  // ← add these three
//
//   // Also add:
//   approval_note?:    string;
//   approval_by?:      string;  // admin user id / name
//   approval_at?:      string;  // ISO timestamp
//
// ─────────────────────────────────────────────────────────────────────────────

// 3. ── In DEFAULT_FORM, add ───────────────────────────────────────────────────
//
//   approval_enabled:              false,
//   approval_send_approved_email:  true,
//   approval_send_rejected_email:  true,
//   approval_require_note:         false,
//   approval_email_field:          '',
//   approval_from_name:            '',
//   approval_email_subject:        'Your submission has been approved',
//   approval_email_body:           '<p>Hi {submitter_name},</p>\n<p>Your submission <strong>#{submission_id}</strong> has been <strong>approved</strong>.</p>\n<p>{approval_note}</p>',
//   rejection_email_subject:       'Update on your submission',
//   rejection_email_body:          '<p>Hi {submitter_name},</p>\n<p>Unfortunately we could not approve submission <strong>#{submission_id}</strong> at this time.</p>\n<p>{rejection_note}</p>',
//
// ─────────────────────────────────────────────────────────────────────────────

// ── FULL UPDATED TYPES (drop-in replacement) ──────────────────────────────────

export type MultiLangValue = Record<string, string>;

export function mlGet(v: MultiLangValue | string | undefined | null, lang: string): string {
  if (!v) return "";
  if (typeof v === "string") {
    // Try to parse JSON string like '{"en":"foo","ar":"bar"}'
    try {
      const parsed = JSON.parse(v);
      if (typeof parsed === "object" && parsed !== null) {
        const val = parsed[lang] ?? parsed["en"] ?? Object.values(parsed)[0];
        return (val !== null && val !== undefined) ? String(val) : "";
      }
    } catch { }
    return v; // plain string
  }
  if (typeof v === "object") {
    // Filter out null/empty — {"en":null} returns "" not null
    const val = v[lang] ?? v["en"] ?? Object.values(v)[0];
    return (val !== null && val !== undefined) ? String(val) : "";
  }
  return "";
}
export function mlSet(v: MultiLangValue | string | undefined, lang: string, text: string): MultiLangValue {
  const base = typeof v === "object" && v !== null ? v : { en: typeof v === "string" ? v : "" };
  return { ...base, [lang]: text };
}
export function mlWrap(v: string | MultiLangValue | undefined, defaultLang = "en"): MultiLangValue {
  if (!v) return { [defaultLang]: "" };
  if (typeof v === "object") return v;
  return { [defaultLang]: v };
}

export interface FieldOptionML {
  label: MultiLangValue;
  selected?: boolean;
}

export type FieldType =
  | 'text' | 'email' | 'phone' | 'number' | 'url' | 'password'
  | 'textarea' | 'select' | 'multiselect' | 'radio' | 'checkbox'
  | 'date' | 'time' | 'datetime' | 'date_range'
  | 'file' | 'signature' | 'hidden' | 'rating' | 'slider'
  | 'heading' | 'paragraph' | 'divider' | 'spacer' | 'html'
  | 'terms_checkbox';

export type FieldWidth = 'full' | 'half';

export interface FieldOption { label: string; value: string; selected?: boolean; }

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  message?: string;
  fileTypes?: string[];
  fileMaxSize?: number;
  // Per-rule multilingual error messages — each key holds a MultiLangValue
  messages?: {
    required?: MultiLangValue;
    minLength?: MultiLangValue;
    maxLength?: MultiLangValue;
    min?: MultiLangValue;
    max?: MultiLangValue;
    email?: MultiLangValue;
    url?: MultiLangValue;
    phone?: MultiLangValue;
    pattern?: MultiLangValue;
    file?: MultiLangValue;
  };
}

export interface ConditionRule {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty';
  value: string;
}

export interface FieldConditions {
  action: 'show' | 'hide';
  logic: 'all' | 'any';
  rules: ConditionRule[];
}

export interface SliderImage {
  label: string;
  img: string;
  value: number;
}

export interface FieldSettings {
  rows?: number;
  multiple?: boolean;
  maxFiles?: number;
  accept?: string;
  showProgress?: boolean;
  enableGoogleDrive?: boolean;
  enableDropbox?: boolean;
  maxStars?: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  showImages?: boolean;
  sliderImages?: SliderImage[];
  height?: number;
  html?: MultiLangValue | string;
  termsText?: string;  // terms_checkbox: the label/HTML shown next to checkbox
  termsRequired?: boolean; // whether agreement is mandatory
  termsUrl?: string;  // optional link to full T&C page
  gateway?: 'stripe' | 'paypal' | 'razorpay' | 'square';
  type?: 'fixed' | 'variable' | 'donation';
  amount?: number;
  currency?: string;
  // Divider section grouping
  sectionLabel?: string;                          // optional heading shown above the section box
  sectionStyle?: 'card' | 'bordered' | 'plain';  // visual style, defaults to 'card'
  sectionIcon?: string;                           // optional emoji shown in the step's progress dot (multi-step forms)
}

export interface FormField {
  id?: number;
  _id?: string;
  step: number;
  type: FieldType;
  label: MultiLangValue;
  placeholder?: MultiLangValue;
  helpText?: MultiLangValue;
  options: FieldOptionML[];
  name: string;
  defaultValue?: string;
  required: boolean;
  cssClass?: string;
  cssId?: string;
  sort_order: number;
  width: FieldWidth;
  validation: FieldValidation;
  has_conditions: boolean;
  conditions: Partial<FieldConditions>;
  settings: FieldSettings;
}

export interface NotificationEmail {
  id?: string;
  name?: string;
  to: string;
  from_name?: string;
  from_email?: string;
  cc?: string;
  bcc?: string;
  subject: string;
  reply_to?: string;
  body: string;
  conditions?: Partial<FieldConditions>;
  active: boolean;
}

export interface FormWebhook {
  id?: string;
  url: string;
  secret?: string;
  events: string[];
  active: boolean;
}

export interface FormCustomStyles {
  primaryColor?: string;
  btnColor?: string;
  btnTextColor?: string;
  labelColor?: string;
  borderColor?: string;
  formBg?: string;
  borderRadius?: number;
  fontFamily?: string;
  fontSize?: number;

  // ADD THESE NEW FIELDS
  maxWidth?: string;

  wrapRadius?: number;
  wrapPadding?: string;

  wrapBorderWidth?: number;
  wrapBorderStyle?: string;
  wrapBorderColor?: string;

  wrapShadow?: string;
  wrapBg?: string;
  logoUrl?: string;
}

export interface PaymentGatewayConfig {
  stripeEnabled?: boolean;
  stripeTestMode?: boolean;
  stripePublishableKey?: string;
  stripeSecretKey?: string;
  stripeWebhookSecret?: string;
  stripeDefaultCurrency?: string;
  stripeDescriptor?: string;
  paypalEnabled?: boolean;
  paypalSandbox?: boolean;
  paypalClientId?: string;   // Live Client ID
  paypalSecret?: string;   // Live Secret
  paypalSandboxClientId?: string;   // Sandbox Client ID
  paypalSandboxSecret?: string;   // Sandbox Secret
  paypalCurrency?: string;
  razorpayEnabled?: boolean;
  razorpayKeyId?: string;
  razorpayKeySecret?: string;
  razorpayWebhook?: string;
  squareEnabled?: boolean;
  squareAppId?: string;
  squareToken?: string;
  squareLocationId?: string;
}

export interface Form {
  id?: number;
  name: MultiLangValue | string;   // per-language form title
  slug?: string;
  description?: MultiLangValue | string;
  submit_btn_text: MultiLangValue | string;
  submit_btn_loading_text: MultiLangValue | string;
  success_message: MultiLangValue | string;
  status: 'active' | 'inactive' | 'archived';
  is_multi_step: boolean;
  steps: { title: string; description?: string; icon?: string }[];
  redirect_url?: string;
  auto_responder_enabled: boolean;
  auto_responder_subject?: MultiLangValue | string;
  auto_responder_template?: MultiLangValue | string;
  auto_responder_from_name?: string;
  notification_emails: NotificationEmail[];
  attach_files_to_notification: boolean;
  captcha_enabled: boolean;
  captcha_type: 'v2' | 'v3' | 'none';
  captcha_site_key?: string;
  captcha_secret_key?: string;
  honeypot_enabled: boolean;
  csrf_protection: boolean;
  rate_limit_per_hour: number;
  ip_blocklist: string[];
  max_file_size_mb: number;
  allowed_file_types: string[];
  store_submissions: boolean;
  show_title: boolean;
  show_description: boolean;
  theme: 'default' | 'minimal' | 'card' | 'bordered';
  custom_styles: FormCustomStyles;
  custom_css?: string;
  webhooks: FormWebhook[];
  integrations: Record<string, unknown>;
  payment_config: PaymentGatewayConfig;
  gdpr_consent_required: boolean;
  gdpr_consent_text?: string;
  data_retention_days: number;
  allow_data_deletion: boolean;
  encrypt_submissions: boolean;

  // ── Page title capture ────────────────────────────────────────────────────
  /** When true, the JS embed captures document.title / URL and sends it as
   *  the hidden field value. Available as {page_title} in emails and headings. */
  capture_page_title?: boolean;
  /** The field name to use for the captured page title (default: page_title) */
  page_title_field_name?: string;
  // ──────────────────────────────────────────────────────────────────────────

  // ── Approval workflow ──────────────────────────────────────────────────────
  approval_enabled?: boolean;
  approval_send_approved_email?: boolean;
  approval_send_rejected_email?: boolean;
  approval_require_note?: boolean;
  /** Name of the form field whose value holds the submitter's email address */
  approval_email_field?: string;
  approval_from_name?: string;
  approval_email_subject?: string;
  approval_email_body?: string;
  rejection_email_subject?: string;
  rejection_email_body?: string;
  // ──────────────────────────────────────────────────────────────────────────

  // ── Registration (Shopify customer creation) ───────────────────────────────
  is_registration_form?: boolean;
  registration_approval_mode?: 'auto' | 'manual';
  registration_tags?: string[];
  /** Shopify customer field key -> this form's own FormField.name */
  registration_field_mapping?: Record<string, string>;
  registration_metafields?: { namespace: string; key: string; type: string; form_field: string }[];
  // ──────────────────────────────────────────────────────────────────────────

  fields: FormField[];
  created_from_scratch?: boolean;
  submissions_count?: number;
  unread_count?: number;
  views_count?: number;
  created_at?: string;
  updated_at?: string;
  /** True when this form is beyond the store's CURRENT plan's form limit
   *  (e.g. created on Pro, still exists after downgrading to Free) — the
   *  API rejects edits to it and it's hidden from the storefront until the
   *  merchant upgrades again or frees a slot by deleting an older form. */
  locked?: boolean;
  /** True when the store's plan doesn't include file-upload fields — the
   *  builder shows "File Upload" locked with an upgrade prompt instead of
   *  letting it be added. Enforced server-side too at every upload endpoint. */
  file_upload_locked?: boolean;
}

export interface FormSubmission {
  id: number;
  form_id: number;
  customer_email?: string;
  customer_id?: string;
  data: Record<string, string | string[]>;
  files?: { field_name: string; original_name: string; path: string; size: number }[];
  source?: string;
  ip?: string;
  is_spam: boolean;
  /** Extended status — 'pending' | 'approved' | 'rejected' added for approval workflow */
  status: 'new' | 'read' | 'starred' | 'archived' | 'spam' | 'pending' | 'approved' | 'rejected';
  approval_note?: string;
  approval_by?: string;
  approval_at?: string;
  email_logs?: { to: string; sent_at: string; status: string }[];
  created_at: string;
  /** Registration forms only — set once syncRegistrationCustomer() succeeds */
  shopify_customer_id?: string | null;
  shopify_customer_synced_at?: string | null;
  /** Set on the most recent sync attempt's failure, cleared on success */
  shopify_sync_error?: string | null;
}

export interface FormAnalytics {
  summary: {
    views: number; starts: number; submissions: number;
    spam: number; conversion_rate: number; emails_sent: number;
  };
  daily: { date: string; views: number; starts: number; submissions: number; spam: number }[];
}

export const FIELD_PALETTE: {
  type: FieldType; label: string; icon: string;
  category: 'input' | 'choice' | 'advanced' | 'layout';
}[] = [
    { type: 'text', label: 'Short Text', icon: 'T', category: 'input' },
    { type: 'email', label: 'Email', icon: '@', category: 'input' },
    { type: 'phone', label: 'Phone', icon: '#', category: 'input' },
    { type: 'number', label: 'Number', icon: '12', category: 'input' },
    { type: 'url', label: 'URL / Link', icon: '↗', category: 'input' },
    { type: 'password', label: 'Password', icon: '●', category: 'input' },
    { type: 'textarea', label: 'Long Text', icon: '¶', category: 'input' },
    { type: 'date', label: 'Date', icon: '📅', category: 'input' },
    { type: 'time', label: 'Time', icon: '⏰', category: 'input' },
    { type: 'datetime', label: 'Date & Time', icon: '📆', category: 'input' },
    { type: 'date_range', label: 'Date Range', icon: '↔', category: 'input' },
    { type: 'select', label: 'Dropdown', icon: '▼', category: 'choice' },
    { type: 'multiselect', label: 'Multi-Select', icon: '☰', category: 'choice' },
    { type: 'radio', label: 'Radio Buttons', icon: '◉', category: 'choice' },
    { type: 'checkbox', label: 'Checkboxes', icon: '☑', category: 'choice' },
    { type: 'rating', label: 'Star Rating', icon: '★', category: 'choice' },
    { type: 'slider', label: 'Slider', icon: '⇔', category: 'choice' },
    { type: 'file', label: 'File Upload', icon: '↑', category: 'advanced' },
    { type: 'signature', label: 'Signature Pad', icon: '✍', category: 'advanced' },
    { type: 'hidden', label: 'Hidden Field', icon: '👁', category: 'advanced' },
    { type: 'terms_checkbox', label: 'Terms & Conditions', icon: '✅', category: 'advanced' },
    { type: 'heading', label: 'Heading', icon: 'H', category: 'layout' },
    { type: 'paragraph', label: 'Paragraph', icon: '¶', category: 'layout' },
    { type: 'divider', label: 'Divider', icon: '—', category: 'layout' },
    { type: 'spacer', label: 'Spacer', icon: '⬜', category: 'layout' },
    { type: 'html', label: 'HTML Block', icon: '<>', category: 'layout' },
  ];

export const LAYOUT_TYPES: FieldType[] = ['heading', 'paragraph', 'divider', 'spacer', 'html'];
export const CHOICE_TYPES: FieldType[] = ['select', 'multiselect', 'radio', 'checkbox'];

export const DEFAULT_FORM: Omit<Form, 'id' | 'slug' | 'created_at' | 'updated_at' | 'submissions_count' | 'unread_count' | 'views_count'> = {
  name: { en: '' },
  description: { en: '' },
  status: 'active',
  is_multi_step: false,
  steps: [],
  submit_btn_text: { en: 'Submit' },
  submit_btn_loading_text: { en: 'Submitting...' },
  success_message: { en: '<p><strong>Thank you!</strong> Your response has been submitted.</p>' },
  redirect_url: '',
  auto_responder_enabled: false,
  auto_responder_subject: 'Thank you for reaching out',
  auto_responder_template: '',
  auto_responder_from_name: '',
  notification_emails: [],
  attach_files_to_notification: false,
  captcha_enabled: false,
  captcha_type: 'none',
  honeypot_enabled: true,
  csrf_protection: true,
  rate_limit_per_hour: 0,
  ip_blocklist: [],
  max_file_size_mb: 10,
  allowed_file_types: [],
  store_submissions: true,
  show_title: true,
  show_description: true,
  theme: 'default',
  custom_styles: {
    primaryColor: '#ed5a29',
    btnColor: '#ed5a29',
    btnTextColor: '#ffffff',
    labelColor: '#374151',
    borderColor: '#e5e7eb',
    formBg: '#ffffff',
    fontFamily: 'poppins',
    fontSize: 14,
    borderRadius: 8,
    maxWidth: '720px',
    wrapRadius: 18,
    wrapPadding: '32px',
    wrapBorderWidth: 0,
    wrapBorderStyle: 'solid',
    wrapBorderColor: '#e5e7eb',
    wrapShadow: 'sm',
    wrapBg: '#ffffff',
    logoUrl: ''
  },
  custom_css: '',
  webhooks: [],
  integrations: {},
  payment_config: {},
  gdpr_consent_required: false,
  gdpr_consent_text: 'I agree to the processing of my personal data.',
  data_retention_days: 0,
  allow_data_deletion: false,
  encrypt_submissions: false,
  capture_page_title: false,
  page_title_field_name: 'page_title',
  // Approval defaults
  approval_enabled: false,
  approval_send_approved_email: true,
  approval_send_rejected_email: true,
  approval_require_note: false,
  approval_email_field: '',
  approval_from_name: '',
  approval_email_subject: 'Your submission has been approved ✓',
  approval_email_body: '<p>Hi {submitter_name},</p>\n<p>Your submission <strong>#{submission_id}</strong> has been <strong>approved</strong>.</p>\n<p>{approval_note}</p>\n<p>Thank you,<br/>The Team</p>',
  rejection_email_subject: 'Update on your submission',
  rejection_email_body: '<p>Hi {submitter_name},</p>\n<p>We regret to inform you that submission <strong>#{submission_id}</strong> could not be approved at this time.</p>\n<p>{rejection_note}</p>\n<p>Please feel free to resubmit or contact us if you have questions.</p>',
  fields: [],
};

export const makeFieldName = (label: string, suffix = '') =>
  (typeof label === 'string'
    ? label
    : Object.values(label as Record<string, string>)[0] ?? 'field'
  ).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/, '') +
  (suffix ? '_' + suffix : '');

export const makeUid = () => Math.random().toString(36).slice(2, 8);